import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Calendar, AlertCircle, AlertTriangle, ChevronDown, ChevronUp, EyeOff, Target } from 'lucide-react';
import { useBriefing } from '../hooks/useDashboard.js';
import { fmtEur } from '../data/salesData.js';

const HIDDEN_KEY = 'briefing_hidden_until';

export function BriefingCard() {
  const { data, isLoading } = useBriefing();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  // "Verberg tot morgen" check
  const hiddenUntil = localStorage.getItem(HIDDEN_KEY);
  if (hiddenUntil && new Date(hiddenUntil) > new Date()) return null;

  if (isLoading || !data) return null;

  const { dagNaam, begroeting, highlights, aandachtspunten, vandaag, openstaand, omzet, doelen } = data as any;

  function verbergTotMorgen() {
    const morgen = new Date();
    morgen.setDate(morgen.getDate() + 1);
    morgen.setHours(0, 0, 0, 0);
    localStorage.setItem(HIDDEN_KEY, morgen.toISOString());
    // Force re-render by setting state — parent will unmount
    window.location.reload();
  }

  return (
    <div className="bg-gradient-to-br from-brand-secondary to-[#1a1a1a] rounded-xl p-5 text-white shadow-sm">
      {/* Header — clickable to collapse */}
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setCollapsed(!collapsed)}>
        <div className="flex items-center gap-2">
          <Sun size={18} className="text-gold" />
          <h3 className="text-sm font-semibold">{begroeting || `Goedemorgen — ${dagNaam}`}</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); verbergTotMorgen(); }}
            className="text-gray-500 hover:text-gray-300 transition-colors"
            title="Verberg tot morgen"
          >
            <EyeOff size={14} />
          </button>
          {collapsed ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronUp size={16} className="text-gray-400" />}
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Highlights */}
          {highlights && highlights.length > 0 && (
            <div className="mt-3 space-y-1">
              {highlights.map((h: string, i: number) => (
                <p key={i} className="text-xs text-white/80 flex items-start gap-2">
                  <span className="text-gold mt-0.5">•</span>
                  {h}
                </p>
              ))}
            </div>
          )}

          {/* Aandachtspunten */}
          {aandachtspunten && aandachtspunten.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5">
              {aandachtspunten.map((a: { type: string; tekst: string }, i: number) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  {a.type === 'URGENT'
                    ? <AlertTriangle size={12} className="text-red-400 flex-shrink-0 mt-0.5" />
                    : <AlertCircle size={12} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  }
                  <span className={a.type === 'URGENT' ? 'text-red-300' : 'text-amber-300'}>{a.tekst}</span>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-white/10 text-xs">
            {/* Omzet progress */}
            <div className="space-y-1">
              <p className="text-gray-400 uppercase tracking-wider text-[10px]">Maandomzet</p>
              <p className="font-medium">{fmtEur(omzet.maandGecreeerd)}</p>
              {omzet.maandDoel > 0 && (
                <div>
                  <div className="w-full bg-white/10 rounded-full h-1.5 mt-1">
                    <div
                      className="h-1.5 rounded-full transition-all"
                      style={{
                        width: `${Math.min(omzet.percentage, 100)}%`,
                        backgroundColor: omzet.percentage >= 80 ? '#22C55E' : omzet.percentage >= 50 ? '#F59E0B' : '#EF4444',
                      }}
                    />
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">{omzet.percentage}% van doel</p>
                </div>
              )}
            </div>

            {/* Doelen mini progress */}
            {doelen && doelen.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-gray-400 uppercase tracking-wider text-[10px]">Doelen</p>
                {doelen.slice(0, 3).map((d: { type: string; voortgang: number }, i: number) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-400 w-14 truncate">{d.type.replace('OMZET_', '').replace('AANTAL_', '').slice(0, 8)}</span>
                    <div className="flex-1 bg-white/10 rounded-full h-1">
                      <div className="h-1 rounded-full" style={{ width: `${Math.min(d.voortgang, 100)}%`, backgroundColor: d.voortgang >= 70 ? '#22C55E' : '#F59E0B' }} />
                    </div>
                    <span className="text-[10px] w-8 text-left">{d.voortgang}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Vandaag agenda */}
          {vandaag.afspraken.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="flex items-center gap-1 mb-1.5">
                <Calendar size={12} className="text-gold" />
                <p className="text-[10px] text-gray-400 uppercase tracking-wider">Vandaag</p>
              </div>
              <div className="space-y-1">
                {vandaag.afspraken.slice(0, 3).map((a: any) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-2 text-xs cursor-pointer hover:bg-white/5 rounded px-1 py-0.5 -mx-1"
                    onClick={() => a.dossierNummer && navigate(`/dossiers/${a.id}`)}
                  >
                    <span className="text-gold font-medium w-12 flex-shrink-0">
                      {new Date(a.tijd).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className="truncate text-white/80">{a.klant || a.titel}</span>
                  </div>
                ))}
                {vandaag.afspraken.length > 3 && (
                  <p className="text-[10px] text-gray-500 pl-1">+{vandaag.afspraken.length - 3} meer</p>
                )}
              </div>
            </div>
          )}

          {/* Openstaande items */}
          {(openstaand.alerts > 0 || openstaand.vorderingen > 0 || openstaand.nieuweLeads > 0) && (
            <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-3 text-xs">
              <AlertCircle size={12} className="text-amber-400 flex-shrink-0" />
              <div className="flex gap-3 flex-wrap">
                {openstaand.alerts > 0 && <span className="text-amber-300">{openstaand.alerts} alerts</span>}
                {openstaand.vorderingen > 0 && <span className="text-amber-300">{openstaand.vorderingen} vorderingen</span>}
                {openstaand.nieuweLeads > 0 && <span className="text-blue-300">{openstaand.nieuweLeads} nieuwe leads</span>}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
